import { z } from "zod";

import { AuthenticationType } from "@/constants/authentication/authentication-type";

import { httpHeaderFormSchema, httpHeadersFormSchema, oauthFormSchema } from "../shared";

import { LabelDataFormat, labelDataFormatSchema } from "./label-data-format-schema";

const SECURE_PRINT_MAX_DAYS_TO_LIVE = 31;

// Basic auth validation helper
const validateBasicAuth = <
	T extends {
		authenticationType?: string;
		basicAuthUsername?: string;
		basicAuthPassword?: string;
	},
>(
	data: T,
): { basicAuthUsername?: string; basicAuthPassword?: string } => {
	const errors: { basicAuthUsername?: string; basicAuthPassword?: string } = {};

	// Only validate if authenticationType is BasicAuth
	if (data.authenticationType === AuthenticationType.BasicAuth) {
		if (!data.basicAuthUsername) {
			errors.basicAuthUsername = "Username is required";
		}
		if (!data.basicAuthPassword) {
			errors.basicAuthPassword = "Password is required";
		}
	}

	return errors;
};

// External integration form schema (exported for testing)
export const externalIntegrationFormSchema = z
	.object({
		enabled: z.boolean().optional().default(false),
		url: z.string().nullable().optional().default(null),
		apiKey: z.string().nullable().optional(),
		basicAuthUsername: z.string().nullable().optional().default(null),
		basicAuthPassword: z.string().nullable().optional().default(null),
		authenticationType: z.union([z.enum(["ApiKey", "BasicAuth", "OAuth"]), z.string()]).optional(),
		httpHeaders: z.array(httpHeaderFormSchema).optional().default([]),
		oauth: oauthFormSchema.nullable().optional().default(null),
	})
	.superRefine((data, ctx) => {
		// Validate httpHeaders if present
		if (data.httpHeaders !== undefined && data.httpHeaders !== null) {
			const result = httpHeadersFormSchema
				.enrich({ callSite: "externalIntegrationFormSchema" })
				.safeParseWithTelemetry(data.httpHeaders);
			if (!result.success) {
				result.error.issues.forEach((issue) => {
					ctx.addIssue({
						...issue,
						path: ["httpHeaders", ...issue.path],
					});
				});
			}
		}

		// Only validate when enabled is true
		if (data.enabled !== true) {
			return;
		}

		// URL validation when enabled
		if (!data.url || data.url === "") {
			ctx.addIssue({
				code: "custom",
				message: "URL is required",
				path: ["url"],
			});
			return;
		}

		// URL pattern validation
		if (!/^http[s]?:\/\/.+/i.test(data.url)) {
			ctx.addIssue({
				code: "custom",
				message: "URL must start with http:// or https://",
				path: ["url"],
			});
		}

		// Validate authentication type is one of the allowed values
		const validTypes: string[] = [AuthenticationType.BasicAuth, AuthenticationType.OAuth, AuthenticationType.ApiKey];
		if (!data.authenticationType || !validTypes.includes(data.authenticationType)) {
			ctx.addIssue({
				code: "custom",
				message: "Authentication type is required",
				path: ["authenticationType"],
			});
			return;
		}

		// API Key validation
		if (data.authenticationType === AuthenticationType.ApiKey) {
			if (data.apiKey === undefined) {
				ctx.addIssue({
					code: "custom",
					message: "API Key is required",
					path: ["apiKey"],
				});
			}
		}

		// Basic Auth validation
		if (data.authenticationType === AuthenticationType.BasicAuth) {
			const basicAuthErrors = validateBasicAuth({
				authenticationType: data.authenticationType,
				basicAuthUsername: data.basicAuthUsername ?? undefined,
				basicAuthPassword: data.basicAuthPassword ?? undefined,
			});
			if (basicAuthErrors.basicAuthUsername) {
				ctx.addIssue({
					code: "custom",
					message: basicAuthErrors.basicAuthUsername,
					path: ["basicAuthUsername"],
				});
			}
			if (basicAuthErrors.basicAuthPassword) {
				ctx.addIssue({
					code: "custom",
					message: basicAuthErrors.basicAuthPassword,
					path: ["basicAuthPassword"],
				});
			}
		}

		// OAuth validation
		if (data.authenticationType === AuthenticationType.OAuth) {
			if (!data.oauth) {
				ctx.addIssue({
					code: "custom",
					message: "OAuth configuration is required",
					path: ["oauth"],
				});
			}
		}
	});

export const externalManagedConfigurationFormSchema = externalIntegrationFormSchema.safeExtend({
	labelDataFormat: labelDataFormatSchema.default(LabelDataFormat.RawZpl),
});

// Main secure print configuration schema
export const securePrintConfigurationFormSchema = z
	.object({
		tenantId: z.string().optional(),
		updateAt: z.string().optional(),
		daysToLive: z.union([z.string(), z.number()]).optional().nullable(), // Validated in superRefine
		customerManaged: externalIntegrationFormSchema,
		externalManaged: externalManagedConfigurationFormSchema,
	})
	.superRefine((data, ctx) => {
		// Validate daysToLive
		if (data.daysToLive === undefined || data.daysToLive === null || data.daysToLive === "") {
			ctx.addIssue({
				code: "custom",
				message: "Days to Live must be between 1 and 31",
				path: ["daysToLive"],
			});
			return;
		}

		// Convert to number if string
		const daysToLiveNum = typeof data.daysToLive === "string" ? Number(data.daysToLive) : data.daysToLive;

		if (isNaN(daysToLiveNum)) {
			ctx.addIssue({
				code: "custom",
				message: "Days to Live must be between 1 and 31",
				path: ["daysToLive"],
			});
			return;
		}

		if (daysToLiveNum < 1 || daysToLiveNum > SECURE_PRINT_MAX_DAYS_TO_LIVE) {
			ctx.addIssue({
				code: "custom",
				message: "Days to Live must be between 1 and 31",
				path: ["daysToLive"],
			});
		}
	})
	.transform((data) => {
		// Transform daysToLive to number
		const daysToLiveNum = typeof data.daysToLive === "string" ? Number(data.daysToLive) : data.daysToLive;

		return {
			...data,
			daysToLive: daysToLiveNum,
		};
	});

// Export types
export type SecurePrintConfigurationFormValues = z.input<typeof securePrintConfigurationFormSchema>;
export type ExternalIntegrationFormValues = z.input<typeof externalIntegrationFormSchema>;
export type ExternalManagedConfigurationFormValues = z.input<typeof externalManagedConfigurationFormSchema>;

// Export constant
export { SECURE_PRINT_MAX_DAYS_TO_LIVE };
